import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth, userApi, type DigestSettingsResponse } from '@/features/auth'
import { getErrorMessage } from '@/lib/axios'
import { toast } from '@/lib/toast'

/** How long editing has to stop before the list is pushed. */
export const KEYWORD_SYNC_DELAY_MS = 1000

/** `\0` cannot occur in a keyword, so joining on it is a collision-free identity. */
const SEP = '\u0000'

/**
 * Mirror this device's keywords to `registered_keywords`, which is the list the
 * morning digest iterates (`backend/app/services/digest_service.py:78`).
 *
 * ## The merge rule: last edit wins, and the mirror is one-directional
 *
 * localStorage stays the only source of truth the app reads. The server copy is
 * write-only from the client's side — **nothing here ever writes a server list back
 * into localStorage**, which is what makes an automatic sync safe to turn on at all.
 * A merge can only delete someone's keywords in the read direction, so there is no
 * read direction.
 *
 * What that costs: with two browsers the digest follows whichever one edited most
 * recently, so a desktop list can be replaced by a shorter phone list. Nothing is
 * deleted anywhere — each browser keeps its own list intact, and only the digest's
 * choice of list moves. `DigestSettings` shows the stored list when it disagrees with
 * this device so the swap is visible rather than silent.
 *
 * The alternatives were worse. Server-wins needs that read direction, and on the
 * first deploy most accounts have an empty stored list (only pressing save ever wrote
 * one) — it would wipe real keywords out of real browsers. Last-modified-wins needs a
 * clock on both sides: the document's `updated_at` is bumped by unrelated writes
 * (`consume_credit`, digest sends) and a browser clock is whatever the user set it to,
 * so the device with the fastest clock deletes the other's edits. Union never deletes
 * but makes deletion impossible — remove a keyword on one device and the other's stale
 * copy restores it — while drifting toward the tier cap.
 *
 * Pushes are debounced and only fire on an actual change: `pushed` starts as whatever
 * this device loaded with, so mounting, re-rendering or signing in never pushes on
 * their own.
 */
export function useKeywordServerSync(keywords: string[]): void {
  const { user } = useAuth()
  const userId = user?.preferences.user_id
  const queryClient = useQueryClient()
  const pushed = useRef(keywords.join(SEP))

  const serialized = keywords.join(SEP)

  useEffect(() => {
    if (!userId) return
    if (serialized === pushed.current) return

    const next = serialized === '' ? [] : serialized.split(SEP)
    const timer = setTimeout(() => {
      // Marked as pushed before the request, not after: a second edit landing while
      // this one is in flight has to be seen as a change against the new list.
      pushed.current = serialized
      userApi
        .saveRegisteredKeywords(userId, next)
        .then(() => {
          // Keep the digest screen's copy of the stored list honest without a refetch.
          queryClient.setQueryData<DigestSettingsResponse>(['digest-settings', userId], (prev) =>
            prev ? { ...prev, registered_keywords: next } : prev
          )
        })
        .catch((error: unknown) => {
          // ponytail: no retry. The common failure is a 403 keyword limit, which
          // retrying cannot fix, and the next edit pushes again anyway. Silence is the
          // one thing not allowed — an autosave nobody sees fail is how the digest
          // quietly keeps using an old list.
          toast.error('키워드를 브리핑에 반영하지 못했어요', getErrorMessage(error))
        })
    }, KEYWORD_SYNC_DELAY_MS)

    return () => clearTimeout(timer)
  }, [userId, serialized, queryClient])
}
